/**
 * Helpers for supporting Vercel preview hosts within the workspace-session model.
 *
 * On Vercel previews `NEXT_PUBLIC_APP_ENV` is `"staging"`, so previews are
 * indistinguishable from real staging by environment alone. Detection is therefore
 * driven entirely by the request host matching `*.vercel.guidecx.io`.
 *
 * Host shape: `<vercelSub>.vercel.guidecx.io`
 *  - `vercel.guidecx.io` is the fixed base
 *  - `<vercelSub>` is the single Vercel-generated label (e.g. `main-os6rslhb4`)
 *
 * Previews do not use a workspace subdomain: a nested
 * `<workspace>.<vercelSub>.vercel.guidecx.io` host has no wildcard-cert coverage,
 * so the session is tied to the bare preview host. The parsing helpers still
 * tolerate a leading workspace label so a stale prefixed URL collapses cleanly.
 */

const VERCEL_BASE = 'vercel.guidecx.io'

/** Canonical base used when normalizing a preview host for backend RPCs. */
const CANONICAL_BASE = 'staging.guidecx.io'

/**
 * Strips an optional protocol and port, returning the lowercased hostname.
 * Returns null for empty/invalid input.
 */
function toHostname(host: string | null | undefined): string | null {
  if (!host) return null
  const trimmed = host.trim()
  if (!trimmed) return null
  try {
    const withProtocol = trimmed.includes('://') ? trimmed : `http://${trimmed}`
    const { hostname } = new URL(withProtocol)
    return hostname.toLowerCase() || null
  } catch {
    return null
  }
}

/** True when `host` is a Vercel preview host (`*.vercel.guidecx.io`). */
export function isVercelPreviewHost(host: string | null | undefined): boolean {
  const hostname = toHostname(host)
  if (!hostname) return false
  return hostname.endsWith(`.${VERCEL_BASE}`)
}

interface ParsedPreviewHost {
  /** The single Vercel-generated label, e.g. `main-os6rslhb4`. */
  vercelSub: string
  /** The workspace label when present, e.g. `thundercats`. */
  workspace?: string
}

/**
 * Parses a Vercel preview host into its workspace and vercel-sub parts.
 * Returns null when `host` is not a preview host.
 */
export function parsePreviewHost(
  host: string | null | undefined,
): ParsedPreviewHost | null {
  const hostname = toHostname(host)
  if (!hostname || !hostname.endsWith(`.${VERCEL_BASE}`)) return null

  const remainder = hostname.slice(0, -`.${VERCEL_BASE}`.length)
  const labels = remainder.split('.').filter(Boolean)

  // The last label is always the vercel sub; anything before is the workspace.
  const vercelSub = labels[labels.length - 1]
  if (!vercelSub) return null

  const workspace = labels.length > 1 ? labels[0] : undefined

  return { vercelSub, workspace }
}

/**
 * Collapses a preview host to the canonical staging host the backend understands.
 * Non-preview hosts are returned unchanged.
 *
 * @example
 *  thundercats.main-xx.vercel.guidecx.io -> thundercats.staging.guidecx.io
 *  main-xx.vercel.guidecx.io             -> staging.guidecx.io
 */
export function normalizeHostForBackend(
  host: string | null | undefined,
): string | undefined {
  if (!isVercelPreviewHost(host)) return host ?? undefined

  const parsed = parsePreviewHost(host)
  if (!parsed) return host ?? undefined

  return parsed.workspace
    ? `${parsed.workspace}.${CANONICAL_BASE}`
    : CANONICAL_BASE
}

/**
 * Resolves the host a redirect should target.
 *
 * On a Vercel preview the platform only gives us a single host, and a nested
 * `workspace.<sub>.vercel.guidecx.io` subdomain has no wildcard-cert coverage
 * (the TLS handshake is reset). So the session is tied to the bare preview host:
 * any workspace label is dropped and the user stays on `<sub>.vercel.guidecx.io`.
 *
 * Off-preview, the canonical per-workspace domain is returned unchanged
 * (`workspace.staging.guidecx.io` / `workspace.guidecx.com`).
 *
 * @example
 *  resolveRedirectHost('thundercats.staging.guidecx.io', 'main-xx.vercel.guidecx.io')
 *    -> main-xx.vercel.guidecx.io
 *  resolveRedirectHost('thundercats.staging.guidecx.io', 'app.staging.guidecx.io')
 *    -> thundercats.staging.guidecx.io
 */
export function resolveRedirectHost(
  canonicalDomain: string | null | undefined,
  currentHost: string | null | undefined,
): string | undefined {
  if (isVercelPreviewHost(currentHost)) {
    const parsed = parsePreviewHost(currentHost)
    if (parsed) return `${parsed.vercelSub}.${VERCEL_BASE}`
  }

  return canonicalDomain ?? undefined
}
